import { useMemo } from "react";
import { visibleAchievements, hasClubReward } from "../../sim/achievements";
import { prebakedIconUrl, type IconKind } from "../../render/prebakedIcon";
import { decorKeyName } from "../../sim/speciesDecor";
import { buildingSkin } from "../../sim/houseSkins";
import { shelfSkin } from "../../sim/shelfSkins";
import { potSkin } from "../../sim/potSkins";
import { fenceStyle } from "../../sim/fenceStyles";

/**
 * THE GARDEN CLUB'S SHARED PIECES — the crest, the drifting petals and the prize spread. They live apart from
 * the /garden-club page (site/GardenClub.tsx) so the IN-GAME club modal (ui/GardenClubModal.tsx) draws the very
 * same identity without pulling the website's nav, footer and router into the game chunk.
 */

// ---------------------------------------------------------------------------
//  the prizes — real reward art, read off the achievement list
// ---------------------------------------------------------------------------

/** One gallery tile: the pre-baked picture and the name the game calls the item by. */
export interface ClubPrize { src: string; name: string }

/** The prize kinds the gallery can show, each named from its own catalogue. Left out on purpose: `frame`
 *  (avatar borders have no pre-baked icon) and `floor` (a floor skin's preview is a thin strip of tiles that
 *  reads as a grey loading bar in a square tile). */
const PRIZE_NAMES: Partial<Record<IconKind, (id: string) => string>> = {
  decor: decorKeyName,
  building: (id) => buildingSkin(id).name,
  shelf: (id) => shelfSkin(id).name,
  pot: (id) => potSkin(id).name,
  fence: (id) => fenceStyle(id).name,
};

/** Every achievement that carries a Garden Club prize, and a spread of their pictures for the gallery. */
export function useClubPrizes(max = 12): { count: number; icons: string[]; prizes: ClubPrize[] } {
  return useMemo(() => {
    const club = visibleAchievements(new Set(), { hideSecret: true }).filter(hasClubReward);
    const all: ClubPrize[] = [];
    const seen = new Set<string>();
    const add = (kind: string, id: string) => {
      // composite ids (`goldStatue:plant:rose` — the per-species trophies of the generated families) are drawn
      // from their species at runtime and have no pre-baked picture; only catalogue items do
      if (id.includes(":")) return;
      const name = PRIZE_NAMES[kind as IconKind];
      const src = name && prebakedIconUrl(kind as IconKind, id);
      if (name && src && !seen.has(src)) { seen.add(src); all.push({ src, name: name(id) }); }
    };
    for (const a of club) {
      for (const u of a.reward?.unlocks ?? []) { const i = u.indexOf(":"); if (i > 0) add(u.slice(0, i), u.slice(i + 1)); }
      for (const id of Object.keys(a.reward?.decor ?? {})) add("decor", id);
    }
    // a SPREAD, not the first dozen: the list is in unlock order, and the first dozen are all early decor
    const step = Math.max(1, all.length / max);
    const prizes: ClubPrize[] = [];
    for (let i = 0; i < all.length && prizes.length < max; i += step) prizes.push(all[Math.floor(i)]!);
    return { count: club.length, icons: prizes.map((p) => p.src), prizes };
  }, [max]);
}

// ---------------------------------------------------------------------------
//  art
// ---------------------------------------------------------------------------

/** THE CLUB CREST — a five-petal blossom inside a lantern-gold ring. Pure SVG so it is crisp at every size. */
export function ClubCrest({ size = 96, glow = false }: { size?: number; glow?: boolean }) {
  const petals = [0, 72, 144, 216, 288];
  return (
    <svg className={`gc-crest${glow ? " glow" : ""}`} width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <radialGradient id="gc-crest-bg" cx="50%" cy="42%" r="60%">
          <stop offset="0%" stopColor="#21453b" /><stop offset="100%" stopColor="#0c1a16" />
        </radialGradient>
        <linearGradient id="gc-crest-ring" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f6dc95" /><stop offset="100%" stopColor="#b8862c" />
        </linearGradient>
        <radialGradient id="gc-crest-petal" cx="50%" cy="85%" r="90%">
          <stop offset="0%" stopColor="#fbe3ef" /><stop offset="55%" stopColor="#f2a9cf" /><stop offset="100%" stopColor="#b5508a" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="46" fill="url(#gc-crest-bg)" stroke="url(#gc-crest-ring)" strokeWidth="4" />
      <circle cx="50" cy="50" r="39" fill="none" stroke="#f1cf7c" strokeOpacity="0.35" strokeWidth="1" strokeDasharray="2 3" />
      {/* two leaves under the bloom */}
      <path d="M50 62 C38 66 30 76 28 84 C38 82 47 74 50 62Z" fill="#4e9a6e" stroke="#0c1a16" strokeWidth="1.4" />
      <path d="M50 62 C62 66 70 76 72 84 C62 82 53 74 50 62Z" fill="#3f8460" stroke="#0c1a16" strokeWidth="1.4" />
      {petals.map((r) => (
        <path key={r} transform={`rotate(${r} 50 46)`} d="M50 46 C42 38 42 24 50 17 C58 24 58 38 50 46Z"
          fill="url(#gc-crest-petal)" stroke="#3a1428" strokeWidth="1.3" strokeLinejoin="round" />
      ))}
      <circle cx="50" cy="46" r="5.5" fill="#f1cf7c" stroke="#5a3d0c" strokeWidth="1.2" />
    </svg>
  );
}

/** Drifting petals behind a hero. Decorative; frozen under reduced motion by the stylesheet. */
export function PetalDrift({ n = 14 }: { n?: number }) {
  return (
    <div className="gc-drift" aria-hidden="true">
      {Array.from({ length: n }, (_, i) => (
        <span key={i} style={{
          ["--x" as string]: `${(i * 37) % 100}%`,
          ["--d" as string]: `${11 + ((i * 7) % 9)}s`,
          ["--delay" as string]: `${-((i * 1.7) % 14)}s`,
          ["--s" as string]: `${0.6 + ((i * 13) % 7) / 10}`,
        }} />
      ))}
    </div>
  );
}

